import { Injectable } from '@angular/core'
import { Subject, takeUntil } from 'rxjs'
import { AuthService } from '../../../services/auth.service'
import { ServerUrlService } from '../../../services/server-url.service'
import { MessageChatSocketService } from './message-chat-socket.service'
import { MessageChatStateService } from './message-chat-state.service'
import { MessageChatService } from './message-chat.service'

@Injectable({ providedIn: 'root' })
export class MessageChatSessionService {
  private initialized = false
  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly authService: AuthService,
    private readonly serverUrl: ServerUrlService,
    private readonly chatService: MessageChatService,
    private readonly socketService: MessageChatSocketService,
    private readonly state: MessageChatStateService
  ) {}

  initialize(): void {
    if (this.initialized) {
      return
    }
    this.initialized = true

    const userId = this.authService.user?.id ?? this.serverUrl.userid
    if (!userId || userId <= 0) {
      this.initialized = false
      return
    }

    this.state.currentUserId.set(userId)
    this.socketService.connect(userId, '')
    this.loadChats()
    this.loadOnlineUsers()
    this.subscribeToGlobalEvents()
  }

  private loadChats(): void {
    this.state.loading.set(true)
    this.chatService
      .findChats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (chats) => {
          this.state.chats.set(chats)
          this.state.loading.set(false)
          this.socketService.joinAllChats(chats.map((c) => c.id))
        },
        error: () => this.state.loading.set(false),
      })
  }

  private loadOnlineUsers(): void {
    this.chatService
      .findOnlineUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ids) => this.state.onlineUserIds.set(new Set(ids)),
      })
  }

  private subscribeToGlobalEvents(): void {
    this.socketService
      .onNewMessage()
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.state.updateChatLastMessage(msg.chatId, msg)
      })

    this.socketService
      .onUserOnline()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ userId }) => this.state.setUserOnline(userId))

    this.socketService
      .onUserOffline()
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ userId }) => this.state.setUserOffline(userId))
  }
}
